import Page from 'components/Page';
import PageDefaultImage from 'components/PageDefaultImage';
import MediaGroupsContainer from 'containers/MediaGroupsContainer';
import { SheetContext } from 'providers/Sheet';
import TRACKING_EVENTS from 'providers/Tracking/constants';
import { TrackingContext } from 'providers/Tracking/index';
import TRACKING_EVENT_COLLECTIONS from 'providers/Tracking/trackingEventCollections';
import { forwardRef, memo, useContext, useEffect, useState } from 'react';

type PageDefaultProps = {
  image: string;
  zoom: string;
  links?: ALink[];
  className?: string;
  visible?: boolean;
  pageId: string;
  type?: string;
  isLastPage?: boolean;
  number: number;
  promotionId?: string;
  promotionTitle?: string;
  altText?: string;
  isAbTestVerticalScrollActive?: boolean;
  width?: number;
  height?: number;
  currentPage?: number;
};
const PageDefault = forwardRef<HTMLLIElement, PageDefaultProps>(
  (
    {
      pageId,
      image,
      zoom,
      links = [],
      className = '',
      visible = false,
      type = 'STANDARD',
      isLastPage = false,
      number,
      promotionId,
      promotionTitle,
      altText,
      isAbTestVerticalScrollActive,
      width,
      height,
      currentPage,
    }: PageDefaultProps,
    ref,
  ) => {
    const [isImageLoaded, setIsImageLoaded] = useState(!isAbTestVerticalScrollActive);

    const handleImageLoad = () => {
      setIsImageLoaded(true);
    };

    const isPromotion = visible && type === 'PROMOTION';
    const { trackEventUniversalAnalytics, trackEventDataLayerService } =
      useContext(TrackingContext);
    const { cSettledPage, cCurrentPage } = useContext(SheetContext);
    const activePage = cSettledPage ?? cCurrentPage ?? currentPage ?? 1;
    const isInteractive = !isAbTestVerticalScrollActive || Math.abs(number - activePage) <= 1;

    useEffect(() => {
      if (isPromotion) {
        trackEventUniversalAnalytics({
          ...TRACKING_EVENTS.FLYER_PROMOTION_SEEN,
          eventLabel: promotionId,
        });
        trackEventDataLayerService(TRACKING_EVENT_COLLECTIONS.FLYER_PROMOTION_SEEN, {
          contentID: promotionId,
          contentLabel: promotionTitle,
        });
      }
    }, [
      isPromotion,
      promotionId,
      promotionTitle,
      trackEventDataLayerService,
      trackEventUniversalAnalytics,
    ]);

    if (!visible && links) {
      //preload animated gifs
      links.forEach((linkItem) => {
        if (linkItem.displayType === 'animated_gif') {
          fetch(linkItem.url, { mode: 'no-cors' });
        }
      });
    }

    return (
      <Page
        ref={ref}
        className={className}
        isLastPage={isLastPage}
        isVisible={visible}
        pageId={pageId}
        type="default"
        isAbTestVerticalScrollActive={isAbTestVerticalScrollActive}>
        <div
          className="page__wrapper"
          tabIndex={-1}
          aria-live="polite"
          style={width && height ? { aspectRatio: `${width} / ${height}` } : undefined}>
          <PageDefaultImage
            image={image}
            zoom={zoom}
            number={number}
            visible={visible}
            altText={altText}
            isAbTestVerticalScrollActive={isAbTestVerticalScrollActive}
            currentPage={currentPage}
            {...(isAbTestVerticalScrollActive && { onLoad: handleImageLoad })}
          />
        </div>
        {(!isAbTestVerticalScrollActive || isImageLoaded) &&
          visible &&
          isInteractive &&
          links?.length > 0 && <MediaGroupsContainer pageId={pageId} />}
      </Page>
    );
  },
);

export default memo(PageDefault);
